import { IApiKey } from '@novu/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEnvironment } from '@/context/environment/hooks';
import { QueryKeys } from '@/utils/query-keys';
import { createScopedApiKey, getApiKeys, regenerateApiKeys, revokeScopedApiKey } from '../api/environments';

export const useFetchApiKeys = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { currentEnvironment } = useEnvironment();

  const query = useQuery<{ data: IApiKey[] }>({
    queryKey: [QueryKeys.getApiKeys, currentEnvironment?._id],
    queryFn: async () => await getApiKeys({ environment: currentEnvironment! }),
    enabled: !!currentEnvironment?._id && enabled,
  });

  return query;
};

export const useRegenerateApiKeys = () => {
  const { currentEnvironment } = useEnvironment();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => regenerateApiKeys({ environment: currentEnvironment! }),
    onSuccess: () => {
      // Invalidate the API keys query to refetch the new keys
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.getApiKeys, currentEnvironment?._id],
      });
    },
  });
};

export const useCreateScopedApiKey = () => {
  const { currentEnvironment } = useEnvironment();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => createScopedApiKey({ environment: currentEnvironment!, name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getApiKeys, currentEnvironment?._id] });
    },
  });
};

export const useRevokeScopedApiKey = () => {
  const { currentEnvironment } = useEnvironment();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => revokeScopedApiKey({ environment: currentEnvironment!, keyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getApiKeys, currentEnvironment?._id] });
    },
  });
};
